import { Loader2, Sparkles, X } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "sonner";

import {
  findCompletedGeneratedAssetFineTuneResult,
  type AssistantGeneratedAssetImageFineTunePreviewReference,
} from "@/lib/assistant-generated-asset-image-fine-tune-previews";
import { trpc } from "@/lib/trpc";
import { Badge, Button } from "@runcheetah/ui";

type ProposalState =
  | { status: "active" }
  | { status: "cancelled" }
  | { status: "queued"; generationTaskId?: string; runsRequired?: number }
  | { status: "completed"; generationTaskId?: string; runsRequired?: number }
  | { status: "failed"; runsRequired?: number; message?: string };

const ACTIVE_PROPOSAL_STATE: ProposalState = { status: "active" };
const PENDING_CHAT_ID = "00000000-0000-4000-8000-000000000000";

export function AssistantGeneratedAssetImageFineTunePreviews({
  brandId,
  chatId,
  references,
  isStale = false,
  upgradeLabel,
  onUpgradeRequired,
  onOpenEditor,
}: {
  brandId: string;
  chatId: string | null;
  references: AssistantGeneratedAssetImageFineTunePreviewReference[];
  isStale?: boolean;
  upgradeLabel?: string;
  onUpgradeRequired?: () => void;
  onOpenEditor?: (reference: AssistantGeneratedAssetImageFineTunePreviewReference) => void;
}) {
  if (references.length === 0) {
    return null;
  }

  return (
    <div className="w-full space-y-3">
      {references.map((reference) => (
        <AssistantGeneratedAssetImageFineTunePreviewBanner
          key={reference.id}
          brandId={brandId}
          chatId={chatId}
          reference={reference}
          isStale={isStale}
          upgradeLabel={upgradeLabel}
          onUpgradeRequired={onUpgradeRequired}
          onOpenEditor={onOpenEditor}
        />
      ))}
    </div>
  );
}

function AssistantGeneratedAssetImageFineTunePreviewBanner({
  brandId,
  chatId,
  reference,
  isStale,
  upgradeLabel,
  onUpgradeRequired,
  onOpenEditor,
}: {
  brandId: string;
  chatId: string | null;
  reference: AssistantGeneratedAssetImageFineTunePreviewReference;
  isStale: boolean;
  upgradeLabel?: string;
  onUpgradeRequired?: () => void;
  onOpenEditor?: (reference: AssistantGeneratedAssetImageFineTunePreviewReference) => void;
}) {
  const intl = useIntl();
  const utils = trpc.useUtils();
  const proposalStorageKey = getProposalStorageKey(brandId, reference.id);
  const [proposalState, setProposalState] = useState(() => loadProposalState(proposalStorageKey));
  const [error, setError] = useState<string | null>(null);
  const cancelled = proposalState.status === "cancelled";
  const queued = proposalState.status === "queued";
  const completed = proposalState.status === "completed";
  const failed = proposalState.status === "failed";
  const opensEditor = Boolean(onOpenEditor);
  const closed = proposalState.status === "active" && isStale;
  const activeGenerationTaskId =
    proposalState.status === "queued" || proposalState.status === "completed"
      ? (proposalState.generationTaskId ?? null)
      : null;
  const persistedRunsRequired =
    proposalState.status === "queued" ||
    proposalState.status === "completed" ||
    proposalState.status === "failed"
      ? (proposalState.runsRequired ?? null)
      : null;
  const quoteQuery = trpc.brandAssistantChat.quoteFineTuneGeneratedAssetImage.useQuery(
    {
      brandId,
      chatId: chatId ?? PENDING_CHAT_ID,
      generatedAssetId: reference.generatedAssetId,
      scheduledPostId: reference.scheduledPostId,
      blockId: reference.blockId,
      instruction: reference.instruction,
    },
    { enabled: Boolean(chatId && !closed && !cancelled && !queued && !opensEditor), retry: false },
  );
  const fineTuneImage = trpc.brandAssistantChat.fineTuneGeneratedAssetImage.useMutation({
    onSuccess: async (data) => {
      const runsRequired = quoteQuery.data?.runsRequired;
      const nextState = {
        status: "queued" as const,
        generationTaskId: data.generationTaskId,
        ...(typeof runsRequired === "number" ? { runsRequired } : {}),
      };
      saveProposalState(proposalStorageKey, nextState);
      setProposalState(nextState);
      setError(null);
      await Promise.all([
        chatId
          ? utils.brandAssistantChat.generatedAssets.invalidate({ brandId, chatId })
          : Promise.resolve(),
        utils.brand.getBillingOverview.invalidate({ brandId }),
      ]);
      toast.success(
        intl.formatMessage({
          id: "assistantGeneratedAssetFineTunePreview.queueSuccess",
          defaultMessage: "Image regeneration started",
        }),
      );
    },
    onError: (mutationError) => setError(mutationError.message),
  });
  const generatedAssetsQuery = trpc.brandAssistantChat.generatedAssets.useQuery(
    { brandId, chatId: chatId ?? PENDING_CHAT_ID },
    {
      enabled: Boolean(chatId && activeGenerationTaskId),
      refetchInterval: queued ? 5_000 : false,
    },
  );
  const quote = quoteQuery.data ?? null;
  const visibleError =
    error ?? (failed ? proposalState.message : null) ?? quoteQuery.error?.message ?? null;
  const hasEnoughRuns =
    quote?.runsRemaining === null ||
    quote?.runsRequired === undefined ||
    quote.runsRemaining >= quote.runsRequired;
  const canOpenEditor = Boolean(!closed && !cancelled && !queued && !completed && onOpenEditor);
  const canConfirm = Boolean(
    chatId && quote && hasEnoughRuns && !closed && !cancelled && !queued && !completed,
  );
  const completedAsset = findCompletedGeneratedAssetFineTuneResult(
    generatedAssetsQuery.data?.assets ?? [],
    activeGenerationTaskId,
  );

  useEffect(() => {
    if (!queued || !completedAsset) {
      return;
    }

    const nextState = {
      status: "completed" as const,
      generationTaskId: completedAsset.generationTaskId,
      ...(typeof persistedRunsRequired === "number" ? { runsRequired: persistedRunsRequired } : {}),
    };
    saveProposalState(proposalStorageKey, nextState);
    setProposalState(nextState);
    if (chatId) {
      void utils.brandAssistantChat.generatedAssets.invalidate({ brandId, chatId });
    }
  }, [
    brandId,
    chatId,
    completedAsset,
    persistedRunsRequired,
    proposalStorageKey,
    queued,
    utils.brandAssistantChat.generatedAssets,
  ]);

  const confirm = () => {
    if (canOpenEditor) {
      onOpenEditor?.(reference);
      return;
    }

    if (!canConfirm || fineTuneImage.isPending || !chatId) {
      return;
    }

    fineTuneImage.mutate({
      brandId,
      chatId,
      generatedAssetId: reference.generatedAssetId,
      scheduledPostId: reference.scheduledPostId,
      blockId: reference.blockId,
      instruction: reference.instruction,
    });
  };

  if (cancelled) {
    return (
      <div className="w-full rounded-3xl border border-border/70 bg-muted/35 px-4 py-3 text-sm text-muted-foreground">
        <FormattedMessage
          id="assistantGeneratedAssetFineTunePreview.cancelled"
          defaultMessage="Image edit proposal cancelled."
        />
      </div>
    );
  }

  return (
    <article className="w-full border-y border-border/60 bg-transparent">
      <div className="space-y-4 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground/88">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center text-muted-foreground">
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
              <FormattedMessage
                id="assistantGeneratedAssetFineTunePreview.title"
                defaultMessage="Regenerate this image?"
              />
            </div>
            <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
              {reference.summary ?? reference.instruction}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {reference.slideIndex ? (
              <Badge
                variant="outline"
                className="rounded-md border-border/70 bg-card/70 text-muted-foreground"
              >
                <FormattedMessage
                  id="assistantGeneratedAssetFineTunePreview.slideBadge"
                  defaultMessage="Slide {slideIndex}"
                  values={{ slideIndex: reference.slideIndex }}
                />
              </Badge>
            ) : null}
            {reference.batchName ? (
              <Badge
                variant="outline"
                className="max-w-48 truncate rounded-md border-border/70 bg-card/70 text-muted-foreground"
              >
                {reference.batchName}
              </Badge>
            ) : null}
          </div>
        </div>

        {!closed && !queued && !completed && !hasEnoughRuns && quote ? (
          <div className="rounded-2xl border border-destructive/25 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            <FormattedMessage
              id="assistantGeneratedAssetFineTunePreview.insufficientRuns"
              defaultMessage="This needs {required} runs, but only {remaining} remain."
              values={{ required: quote.runsRequired, remaining: quote.runsRemaining ?? 0 }}
            />
          </div>
        ) : null}
        {!closed && !queued && !completed && visibleError ? (
          <div className="rounded-2xl border border-destructive/25 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {visibleError}
          </div>
        ) : null}

        {closed ? (
          <ProposalStatus>
            <X className="text-muted-foreground/40" size="16" aria-hidden="true" />
            <span className="font-medium text-foreground/58">
              <FormattedMessage
                id="assistantGeneratedAssetFineTunePreview.supersededLabel"
                defaultMessage="This proposal can no longer be accepted because the conversation has moved forward."
              />
            </span>
          </ProposalStatus>
        ) : queued ? (
          <ProposalStatus>
            <Sparkles className="text-muted-foreground/40" size="16" aria-hidden="true" />
            <span className="min-w-0 truncate bg-[linear-gradient(90deg,var(--muted-foreground)_0%,var(--muted-foreground)_34%,var(--primary)_50%,var(--muted-foreground)_66%,var(--muted-foreground)_100%)] bg-[length:220%_100%] bg-clip-text text-transparent motion-safe:animate-[assistant-status-aurora_2.2s_linear_infinite]">
              <FormattedMessage
                id="assistantGeneratedAssetFineTunePreview.generatingImageEdit"
                defaultMessage="Regenerating Image"
              />
            </span>
            {typeof persistedRunsRequired === "number" ? (
              <RunsUsed count={persistedRunsRequired} />
            ) : null}
          </ProposalStatus>
        ) : completed ? (
          <ProposalStatus>
            <Sparkles className="text-muted-foreground/40" size="16" aria-hidden="true" />
            <span className="min-w-0 text-muted-foreground">
              <FormattedMessage
                id="assistantGeneratedAssetFineTunePreview.generatedImageEdit"
                defaultMessage="Image Regenerated"
              />
            </span>
            {typeof persistedRunsRequired === "number" ? (
              <RunsUsed count={persistedRunsRequired} />
            ) : null}
          </ProposalStatus>
        ) : (
          <div className="space-y-3 border-t border-border/60 pt-3">
            {failed ? (
              <div className="text-sm text-muted-foreground">
                <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                  <X className="text-destructive/70" size="16" aria-hidden="true" />
                  <span className="font-medium text-destructive">
                    <FormattedMessage
                      id="assistantGeneratedAssetFineTunePreview.failedLabel"
                      defaultMessage="Image edit failed"
                    />
                  </span>
                  {typeof persistedRunsRequired === "number" ? (
                    <RunsUsed count={persistedRunsRequired} />
                  ) : null}
                </div>
              </div>
            ) : null}
            <div className="flex shrink-0 flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="ghost"
                className="rounded-full text-foreground/65"
                disabled={fineTuneImage.isPending}
                onClick={() => {
                  saveProposalState(proposalStorageKey, { status: "cancelled" });
                  setProposalState({ status: "cancelled" });
                }}
              >
                <X className="h-4 w-4" aria-hidden="true" />
                <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
              </Button>
              {!opensEditor && !hasEnoughRuns && onUpgradeRequired ? (
                <Button type="button" className="rounded-full" onClick={onUpgradeRequired}>
                  {upgradeLabel ?? (
                    <FormattedMessage id="runConfirmation.upgrade" defaultMessage="Upgrade" />
                  )}
                </Button>
              ) : (
                <Button
                  type="button"
                  className="rounded-full"
                  disabled={opensEditor ? !canOpenEditor : !canConfirm || fineTuneImage.isPending}
                  onClick={confirm}
                >
                  {fineTuneImage.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : null}
                  {opensEditor ? (
                    <FormattedMessage
                      id="assistantGeneratedAssetFineTunePreview.openEditor"
                      defaultMessage="Open editor"
                    />
                  ) : quote ? (
                    <FormattedMessage
                      id="assistantGeneratedAssetFineTunePreview.confirmWithRuns"
                      defaultMessage="Regenerate Image for {runCount, plural, =0 {free} one {# run} other {# runs}}"
                      values={{ runCount: quote.runsRequired }}
                    />
                  ) : quoteQuery.isPending || quoteQuery.isFetching ? (
                    <FormattedMessage
                      id="assistantGeneratedAssetFineTunePreview.checkingRuns"
                      defaultMessage="Checking runs..."
                    />
                  ) : (
                    <FormattedMessage
                      id="assistantGeneratedAssetFineTunePreview.confirm"
                      defaultMessage="Regenerate Image"
                    />
                  )}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

function ProposalStatus({ children }: { children: ReactNode }) {
  return (
    <div className="border-t border-border/60 pt-3 text-sm text-muted-foreground">
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">{children}</div>
    </div>
  );
}

function RunsUsed({ count }: { count: number }) {
  return (
    <>
      <span className="h-1 w-1 rounded-full bg-muted-foreground/42" aria-hidden="true" />
      <span className="font-medium text-foreground/58">
        <FormattedMessage
          id="assistantGeneratedAssetFineTunePreview.runsUsed"
          defaultMessage="{count, plural, =0 {Free} one {# run used} other {# runs used}}"
          values={{ count }}
        />
      </span>
    </>
  );
}

function getProposalStorageKey(brandId: string, proposalId: string) {
  return `assistant-generated-asset-image-fine-tune:${brandId}:${proposalId}`;
}

function loadProposalState(key: string): ProposalState {
  if (typeof window === "undefined") {
    return ACTIVE_PROPOSAL_STATE;
  }

  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      return ACTIVE_PROPOSAL_STATE;
    }

    const parsed = JSON.parse(raw) as ProposalState;
    if (
      parsed.status === "active" ||
      parsed.status === "cancelled" ||
      parsed.status === "queued" ||
      parsed.status === "completed" ||
      parsed.status === "failed"
    ) {
      return parsed;
    }
  } catch {
    return ACTIVE_PROPOSAL_STATE;
  }

  return ACTIVE_PROPOSAL_STATE;
}

function saveProposalState(key: string, state: ProposalState) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // Losing persisted proposal state should not block regeneration.
  }
}
